import store, { useAppSelector } from "../../redux/store";
import { useCallback, useMemo } from "react";
import Article from "../../models/menu/Article";
import { selectMenuTagFilters } from "../../redux/selectors/selectMenuTagFilters";
import { selectArticleArticlegroupsMap } from "../../redux/selectors/selectArticleArticlegroupsMap";
import { selectArticlegroupsMap } from "../../redux/selectors/selectArticlegroupsMap";
import { useIntl } from "react-intl";
import Fuse, { IFuseOptions } from "fuse.js";
import { Language } from "../../../../types/shared/language.ts";
import useMenuFilters from "./useMenuFilters.ts";
import { useArticleTraitFiltering } from "./useArticleTraitFiltering.ts";

type SearchableArticle = { article: Article; name: Article["name"]; translationNames: string[] };

const fuseOptions: IFuseOptions<SearchableArticle> = {
  keys: [
    { name: "name", weight: 1 },
    { name: "translationNames", weight: 1 },
    { name: "translationDescriptions", weight: 0.7 },
    { name: "translationDetailedDescription", weight: 0.7 },
    { name: "articlegroupNames", weight: 0.7 },
    { name: "menuTags", weight: 0.7 },
  ],
  threshold: 0.4,
  includeScore: false,
};

export default function useArticleSearchFiltering(allArticles?: Article[]) {
  const articleArticlegroupsMap = useAppSelector(selectArticleArticlegroupsMap);
  const articleFilter = useMenuFilters();
  const { articleTraitFilter } = useArticleTraitFiltering();
  const articlegroupsMap = useAppSelector(selectArticlegroupsMap);
  const intl = useIntl();
  const menuTagFilters = useAppSelector((state) => selectMenuTagFilters(state, intl));

  const createList = useCallback(
    (allArticles: Article[]) => {
      return allArticles
        .filter((article: Article) => {
          const record = articleArticlegroupsMap[article.id];
          return record && record.length > 0;
        })
        .filter(articleFilter)
        .filter(articleTraitFilter)
        .filter((article) =>
          (articleArticlegroupsMap[article.id] ?? []).some((articlegroupId) => {
            const articlegroup = articlegroupsMap[articlegroupId];
            return articlegroup && !articlegroup.disableOrdering && articlegroup.showInCategoryMenu;
          })
        )
        .map((article) => ({
          article: article,
          name: article.name,
          translationNames: (Object.keys(article.translations ?? {}) as Language[])
            .map((lang) => article.translations?.[lang]?.naam)
            .filter((a): a is string => Boolean(a)),
          articlegroupNames: (articleArticlegroupsMap[article.id] ?? []).map((articlegroupId) => {
            const articlegroup = articlegroupsMap[articlegroupId];
            return [
              articlegroup?.name,
              ...(Object.keys(articlegroup?.translations ?? {}) as Language[])
                .map((lang) => articlegroup?.translations?.[lang]?.naam)
                .filter((a): a is string => Boolean(a)),
              ...(Object.keys(articlegroup?.translations ?? {}) as Language[])
                .map((lang) => articlegroup?.translations?.[lang]?.omschrijving)
                .filter((a): a is string => Boolean(a)),
              ...(Object.keys(articlegroup?.translations ?? {}) as Language[])
                .map((lang) => articlegroup?.translations?.[lang]?.bigdescr)
                .filter((a): a is string => Boolean(a)),
            ].filter((a): a is string => Boolean(a));
          }),
          translationDescriptions: (Object.keys(article.translations ?? {}) as Language[])
            .map((lang) => article.translations?.[lang]?.omschrijving)
            .filter((a): a is string => Boolean(a)),
          translationDetailedDescription: (Object.keys(article.translations ?? {}) as Language[])
            .map((lang) => article.translations?.[lang]?.detailed_description)
            .filter((a): a is string => Boolean(a)),
          menuTags: article.menuTagIds.map((menuTagId) => menuTagFilters[menuTagId]?.name),
        }));
    },
    [articleArticlegroupsMap, articleFilter, articleTraitFilter, articlegroupsMap, menuTagFilters]
  );

  const listForIndex = useMemo(() => {
    return allArticles ? createList(allArticles) : null;
  }, [allArticles, createList]);

  const myIndex = useMemo(() => {
    return listForIndex ? Fuse.createIndex<SearchableArticle>(fuseOptions.keys ?? [], listForIndex) : null;
  }, [listForIndex]);

  return useCallback(
    (articles?: Article[]): Article[] => {
      const articleSearchValue = store.getState().global.articleSearchText;
      // console.log("SEARCHING: ", articleSearchValue);
      if (articles) {
        articles = articles.filter((article) =>
          (articleArticlegroupsMap[article.id] ?? []).some((articlegroupId) => {
            const articlegroup = articlegroupsMap[articlegroupId];
            return articlegroup && !articlegroup.disableOrdering && articlegroup.showInCategoryMenu;
          })
        );
      }

      if (!articleSearchValue) {
        return articles ?? allArticles ?? [];
      }
      if (articles) {
        const list = createList(articles);
        const fuse = new Fuse(list, fuseOptions);

        return fuse.search(articleSearchValue).map((result) => {
          return result.item.article;
        });
      } else if (listForIndex && myIndex) {
        // console.log("USING INDEX");
        const fuse = new Fuse(listForIndex, fuseOptions, myIndex);

        return fuse.search(articleSearchValue).map((result) => {
          return result.item.article;
        });
      } else {
        return [];
      }
    },
    [listForIndex, myIndex, articleArticlegroupsMap, articlegroupsMap, allArticles, createList]
  );
}
